import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import {
  LiteratureReference,
  TranscriptChunk,
} from '@/shared/api/transcriptor/transcriptorApi';
import { trackEvent } from '@/shared/lib';
import { Button } from '@/shared/ui';

import { ConsultationNode, dentalConsultationMessages } from '../../config';
import { useConsultationEdit } from '../../hooks';
import { ConsultationSection } from '../ConsultationSection/ConsultationSection';

import styles from './ConsultationWidget.module.scss';

type ConsultationWidgetProps = {
  data: ConsultationNode[];
  showScores?: boolean;
  showActions?: boolean;
  widgetType?: 'summary' | 'followUp';
  transcriptChunks?: TranscriptChunk[];
  literatureReferences?: LiteratureReference[] | null;
  onSave?: (data: ConsultationNode[]) => Promise<void>;
};

export const ConsultationWidget: FC<ConsultationWidgetProps> = ({
  data,
  showActions = false,
  widgetType,
  transcriptChunks,
  literatureReferences,
  onSave,
}) => {
  const { formatMessage } = useIntl();
  const {
    isEditing,
    editedData,
    showCopySuccess,
    handleEdit,
    handleCopy,
    handleTextChange,
  } = useConsultationEdit({ initialData: data, onSave });

  return (
    <div className={styles.container} data-component="consultation-content">
      {editedData.map((node, nodeIndex) => {
        const { children, title, titleKey } = node;
        const hasNodeActions = showActions && nodeIndex === 0;

        return (
          <div key={nodeIndex} className={styles.node}>
            {(title || hasNodeActions) && (
              <div className={styles.nodeHeader}>
                {title && (
                  <h3 className={cn('h5', styles.nodeTitle)}>
                    {titleKey
                      ? formatMessage({
                          id: titleKey,
                          defaultMessage: title,
                        })
                      : title}
                  </h3>
                )}
                {hasNodeActions && (
                  <div className={styles.nodeActions}>
                    <Button
                      variant={isEditing ? 'primary' : 'secondary'}
                      size="small"
                      onClick={() => {
                        if (!isEditing && widgetType) {
                          if (widgetType === 'summary') {
                            trackEvent({
                              eventName:
                                'TranscriptorReportPageSummaryEditButtonClicked',
                            });
                          } else if (widgetType === 'followUp') {
                            trackEvent({
                              eventName:
                                'TranscriptorReportPageFollowUpEditButtonClicked',
                            });
                          }
                        }

                        handleEdit();
                      }}
                    >
                      {formatMessage(
                        isEditing
                          ? dentalConsultationMessages.saveButton
                          : dentalConsultationMessages.editButton,
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      success={showCopySuccess}
                      onClick={() => {
                        if (widgetType) {
                          if (widgetType === 'summary') {
                            trackEvent({
                              eventName:
                                'TranscriptorReportPageSummaryCopyButtonClicked',
                            });
                          } else if (widgetType === 'followUp') {
                            trackEvent({
                              eventName:
                                'TranscriptorReportPageFollowUpCopyButtonClicked',
                            });
                          }
                        }

                        handleCopy();
                      }}
                    >
                      {formatMessage(
                        showCopySuccess
                          ? dentalConsultationMessages.copiedMessage
                          : dentalConsultationMessages.copyButton,
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className={styles.dataList}>
              {children.map((section, sectionIndex) => (
                <ConsultationSection
                  key={sectionIndex}
                  section={section}
                  nodeIndex={nodeIndex}
                  sectionIndex={sectionIndex}
                  isEditing={isEditing}
                  transcriptChunks={transcriptChunks}
                  literatureReferences={literatureReferences}
                  handleTextChange={handleTextChange}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
